import { useMemo, useState } from "react"
import { useSWRConfig } from "swr"
import { Pencil, Trash2 } from "lucide-react"
import { AddProVenueInstanceDialog } from "@/components/smithers/add-provenue-instance-dialog"
import { EditProVenueInstanceDialog } from "@/components/smithers/edit-provenue-instance-dialog"
import { DeleteProVenueInstanceDialog } from "@/components/smithers/delete-provenue-instance-dialog"
import { useToast } from "@/hooks/use-toast"
import type { SharedProVenueInstance } from "@/types/provenue-instance"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { DeviceSearchBar } from "@/components/alfred/device-search-bar"
import { SortableHeader, type SortDirection } from "@/components/shared/sortable-header"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useProVenueInstances } from "@/hooks/use-provenue-instances"
import { cn } from "@/lib/utils"

type SortColumn = "id" | "name" | "hostName"

export function SmithersProVenueInstancesPage() {
  const { data: instances = [], error, isLoading, mutate } = useProVenueInstances("smithers")
  const [searchInput, setSearchInput] = useState("")
  const [filter, setFilter] = useState("")
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")
  const visibleInstances = useMemo(() => {
    const query = filter.toLowerCase()
    const filtered = instances.filter((instance) => [String(instance.id), instance.name, instance.hostName].some((value) => value.toLowerCase().includes(query)))
    if (sortColumn !== null) {
      filtered.sort((a, b) => {
        const comparison = sortColumn === "id" ? a.id - b.id : a[sortColumn].localeCompare(b[sortColumn])
        return sortDirection === "asc" ? comparison : -comparison
      })
    }
    return filtered
  }, [instances, filter, sortColumn, sortDirection])
  const clearSearch = () => { setSearchInput(""); setFilter("") }
  const handleSort = (column: SortColumn) => {
    setSortDirection(sortColumn === column && sortDirection === "asc" ? "desc" : "asc")
    setSortColumn(column)
  }
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<SharedProVenueInstance | null>(null)
  const [deleting, setDeleting] = useState<SharedProVenueInstance | null>(null)
  const { mutate: mutateCache } = useSWRConfig()
  const { toast } = useToast()
  const onCreated = async (instance: SharedProVenueInstance) => {
    await mutate((current = []) => [...current.filter((item) => item.id !== instance.id), instance].sort((a, b) => a.name.localeCompare(b.name)), { revalidate: false })
    toast({ variant: "success", title: "ProVenue instance created", description: `${instance.name} has been added.` })
    setAdding(false)
  }
  const onUpdated = async (instance: SharedProVenueInstance) => {
    await mutate((current = []) => current.map((item) => item.id === instance.id ? instance : item).sort((a, b) => a.name.localeCompare(b.name)), { revalidate: false })
    await mutateCache((key) => Array.isArray(key) && key[0] === "smithers" && (key[1] === "devices" || key[1] === "global"), undefined, { revalidate: true })
    toast({ variant: "success", title: "ProVenue instance updated", description: `${instance.name} has been updated.` })
    setEditing(null)
  }
  const onDeleted = async (instance: SharedProVenueInstance) => {
    await mutate((current = []) => current.filter((item) => item.id !== instance.id), { revalidate: false })
    toast({ variant: "success", title: "ProVenue instance deleted", description: `${instance.name} has been deleted.` })
    setDeleting(null)
  }

  return (
    <>
      <DashboardHeader onAddProVenueInstance={() => setAdding(true)} />
      {adding && <AddProVenueInstanceDialog onClose={() => setAdding(false)} onCreated={onCreated} />}
      {editing && <EditProVenueInstanceDialog key={editing.id} instance={editing} onClose={() => setEditing(null)} onUpdated={onUpdated} />}
      {deleting && <DeleteProVenueInstanceDialog instance={deleting} onClose={() => setDeleting(null)} onDeleted={onDeleted} />}
      <main className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div>
          <h1 className="mb-2 text-2xl font-semibold text-foreground">ProVenue Instance Manager</h1>
          <p className="text-sm text-muted-foreground">Shared ProVenue instances for Smithers.</p>
        </div>
        <DeviceSearchBar
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() => setFilter(searchInput.trim())}
          onClear={clearSearch}
          activeFilter={filter}
          placeholder="Search instances by ID, name, or host name..."
          disabled={isLoading}
        />
        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn’t load ProVenue instances</AlertTitle>
            <AlertDescription>
              <p>{error instanceof Error ? error.message : "Please try again."}</p>
              <Button variant="outline" onClick={() => void mutate()}>Retry</Button>
            </AlertDescription>
          </Alert>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="table-scroll-x overflow-x-auto">
                <Table aria-label="ProVenue instances" aria-busy={isLoading} className="min-w-max w-full">
                  <TableHeader>
                    <TableRow className="hover:bg-card">
                      <SortableHeader label="ID" sortKey="id" currentSortColumn={sortColumn} currentSortDirection={sortDirection} onSort={handleSort} disabled={isLoading} />
                      <SortableHeader label="Name" sortKey="name" currentSortColumn={sortColumn} currentSortDirection={sortDirection} onSort={handleSort} disabled={isLoading} />
                      <SortableHeader label="Host Name" sortKey="hostName" currentSortColumn={sortColumn} currentSortDirection={sortDirection} onSort={handleSort} disabled={isLoading} />
                      <TableHead className="sticky right-0 bg-card w-24">Edit / Delete</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? Array.from({ length: 3 }, (_, index) => (
                      <TableRow key={index}>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-64" /></TableCell>
                        <TableCell className="sticky right-0 bg-card"><Skeleton className="h-8 w-16" /></TableCell>
                      </TableRow>
                    )) : instances.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-12 text-center">
                          <p className="font-medium">No ProVenue instances yet</p>
                          <p className="text-muted-foreground">Use Add Instance to create a shared ProVenue connection.</p>
                        </TableCell>
                      </TableRow>
                    ) : visibleInstances.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-12 text-center">
                          <p className="font-medium">No matching ProVenue instances</p>
                          <p className="mb-4 text-muted-foreground">Try a different ID, name, or host name.</p>
                          <Button variant="outline" onClick={clearSearch}>Clear search</Button>
                        </TableCell>
                      </TableRow>
                    ) : visibleInstances.map((instance, index) => (
                      <TableRow key={instance.id} className={cn(index % 2 === 1 && "bg-muted/30")}>
                        <TableCell>{instance.id}</TableCell>
                        <TableCell className="font-medium">{instance.name}</TableCell>
                        <TableCell>{instance.hostName}</TableCell>
                        <TableCell className="sticky right-0 bg-card min-w-24">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" aria-label={`Edit ProVenue instance ${instance.name}`} onClick={() => setEditing(instance)} className="size-8">
                              <Pencil data-icon="inline-start" />
                            </Button>
                            <Button variant="ghost" size="icon" aria-label={`Delete ProVenue instance ${instance.name}`} onClick={() => setDeleting(instance)} className="size-8 hover:text-destructive">
                              <Trash2 data-icon="inline-start" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </>
  )
}
